import { Record } from 'immutable';
import { isEmpty } from 'lodash';
import pluralize from 'pluralize';

import {
  getHeadersFromRawData,
  getRowsFromRawData,
  getRowsFromRankedData,
  getHeadersFromRankedRawData,
  formatCell
} from '../utils';

class Answer extends Record(
  {
    id: null,
    questionId: null,
    body: null,
    error: null,
    schemas: [],
    rawAnswer: null,
    formattedAnswer: null,
    answerFeedback: [],
    columnOrder: [],
    type: 'text',
    createdAt: null,
    generatedBy: null,
    vegaSpec: null,
    rawSql: null,
    queryExplanation: null,
    formattingHints: null,
    globalQueryScore: null
  },
  'Answer'
) {
  get hasTableView() {
    return this.rawAnswer !== null && !isEmpty(this.rawAnswer);
  }

  get isRankedTableView() {
    return Boolean(this.displayAnswer && this.displayAnswer.columns && this.displayAnswer.index);
  }

  get isCanned() {
    if (this.generatedBy === 'ClarifyQuestionProcessor') {
      return false;
    }
    if (this.generatedBy === 'EntityRecognitionProcessor') {
      return false;
    }

    if (this.generatedBy.endsWith('pipeline')) {
      return false;
    }

    if (['q2q_llm', 'human_at_supper'].includes(this.generatedBy)) {
      return false;
    }

    return true;
  }

  get dataDescription() {
    if (this.isCanned) return 'No data available';
    if (this.vegaSpec !== null) return 'Generated a chart';
    if (this.data === null) return 'No results found';
    if (this.hasTableView) {
      return `Table with ${this.data.rows.length} ${pluralize('row', this.data.rows.length)} and ${
        this.data.headers.length
      } ${pluralize('column', this.data.headers.length)}`;
    }
    return null;
  }

  get displayAnswer() {
    return this.rawAnswer;
  }

  get data() {
    let headers = null;
    let rows = null;

    if (this.displayAnswer && this.isRankedTableView) {
      headers = getHeadersFromRankedRawData(this.displayAnswer);
      rows = getRowsFromRankedData(this.displayAnswer);
    } else if (this.rawAnswer && this.hasTableView) {
      headers = getHeadersFromRawData(Object.keys(this.displayAnswer));
      rows = getRowsFromRawData(
        Object.values(this.displayAnswer)?.map((row) => Object.values(row)),
        headers
      );
    }

    if (!headers) return null;

    return { headers, rows: rows ?? [] };
  }

  get sanitizedBody() {
    return this.body?.replace(/<\/?[^>]+(>|$)/g, '');
  }

  get isError() {
    return this.globalQueryScore === -2;
  }

  get isSingleValueAnswer() {
    return (
      !this.schemas?.includes('metrics') &&
      this?.data?.rows?.length === 1 &&
      this?.data?.headers?.length === 1
    );
  }

  // Check if this is one row answer with multiple columns
  get isMetricsSingleValueAnswer() {
    return (
      this.schemas?.includes('metrics') &&
      this?.data?.rows?.length === 1 &&
      this.data.rows[0]?.fields &&
      Object.keys(this.data.rows[0].fields).length === this?.data?.headers?.length
    );
  }

  get getMetricsSingleValueAnswer() {
    const fields = this?.data?.rows?.[0]?.fields;
    const headers = this?.data?.headers;

    // First, try to use the 'value' property if it exists
    let value = fields?.value;
    let field = 'value';

    // If 'value' is not present, search for any valid numeric property
    if (value === undefined) {
      field = Object.keys(fields).find((key) => Number.isFinite(fields[key]));
      value = fields[field];
    }

    // Apply formatting hints if available
    if (this.formattingHints) {
      const header = headers?.find((h) => h.column === field);
      if (header) {
        return formatCell({ value, colDef: { field } }, this.formattingHints);
      }
    }

    // Fallback to default formatting
    if (Number.isFinite(value) && value !== Math.floor(value)) {
      return Intl.NumberFormat('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      }).format(value);
    }
    return value;
  }

  get getSingleValueAnswer() {
    const fields = this?.data?.rows?.[0]?.fields;
    const headers = this?.data?.headers;
    if (!fields) return null;
    const field = Object?.keys(fields)?.[0];
    const value = fields?.[field];

    // Apply formatting hints if available
    if (this?.formattingHints && field) {
      const header = headers?.find((h) => h.column === field);
      if (header) {
        return formatCell({ value, colDef: { field } }, this.formattingHints);
      }
    }

    // Fallback to default formatting
    if (Number.isFinite(value) && value !== Math.floor(value)) {
      return Intl.NumberFormat('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      }).format(value);
    }
    return value;
  }
}

export { Answer };
